<script setup>
import { ref, onMounted, watch, toRaw, nextTick } from "vue";

import { useCurrentMenuStore } from "../../stores/current-menu";
import MenuItemModal from "../../components/modals/MenuItemModal";
import MenuItem from "../../components/menus/MenuItem";
import ConfirmModalVue from "../modals/ConfirmModal.vue";

import draggable from "vuedraggable";

const props = defineProps({
    subcategory: {},
    categoryIndex: {
        required: true,
    },
    subcategoryIndex: {
        required: true,
    },
    type: {
        required: true,
    }
});

const renderComponent = ref(true);
const renderChildren = ref(true);

const forceRerender = async (args) => {

    // Remove MyComponent from the DOM
    renderComponent.value = false;

    // Wait for the change to get flushed to the DOM
    await nextTick();

    // Add the component back in
    renderComponent.value = true;
};

const currentMenuStore = useCurrentMenuStore();

const editing = ref(false);
const collapsed = ref(false);

const tempName = ref("");

function addItem(item) {
    item.active = true;

    const rawItem = toRaw(item);

    currentMenuStore[currentMenuStore.currentTab][props.categoryIndex].contents[
        props.subcategoryIndex
    ].contents.unshift(rawItem);

    forceRerender();
}

onMounted(async () => {
    document.addEventListener("mousedown", function (e) {
        if (
            editing.value &&
            e.target.id !== `subcategory-title-${props.subcategoryIndex}` &&
            tempName.value
        ) {
            editing.value = false;
        }
    });

    await nextTick();
    active.value = props.subcategory.active;
});

function deleteSubcategory() {
    renderChildren.value = false;

    currentMenuStore[currentMenuStore.currentTab][props.categoryIndex].contents.splice(
        props.subcategoryIndex,
        1
    );

    renderChildren.value = true;

    return false;
}

const active = ref(true);

watch(active, (value) => {
    // Check categoryIndex and subcategoryIndex are set
    try {
        currentMenuStore[currentMenuStore.currentTab][props.categoryIndex].contents[props.subcategoryIndex].active = value;
    }
    catch (error) {
        console.log(error);
    }
});

watch(editing, (value) => {

    const removeSpecialChars = tempName.value.replace(/[^a-zA-Z0-9 ]/g, '');

    if (value) {
        tempName.value = currentMenuStore[currentMenuStore.currentTab][props.categoryIndex].contents[props.subcategoryIndex].name;
    } else {
        currentMenuStore[currentMenuStore.currentTab][props.categoryIndex].contents[props.subcategoryIndex].name = removeSpecialChars;
    }
});

</script>

<template>
    <div class="menu-item-holder menu-item-holder--subcategory" v-if="renderComponent">
        <div class="menu-item-holder__header">
            <div class="d-flex">
                <div class="handle category-handle">
                    <svg viewBox="0 0 23 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd" clip-rule="evenodd"
                            d="M0 .5A.5.5 0 0 1 .5 0h21.92a.5.5 0 1 1 0 1H.5A.5.5 0 0 1 0 .5ZM0 6.5A.5.5 0 0 1 .5 6h21.92a.5.5 0 1 1 0 1H.5a.5.5 0 0 1-.5-.5Z"
                            fill="currentColor"></path>
                    </svg>
                </div>
                <input type="checkbox" v-model="active" />
                <p class="can-edit" v-if="!editing" @click="editing = !editing"
                    :id="'subcategory-title-' + subcategoryIndex">
                    {{ subcategory.name }}
                </p>
                <input type="text" v-model="tempName" v-if="editing" :id="'subcategory-title-' + subcategoryIndex" />
            </div>
            <div class="menu-item-holder__header--right">
                <MenuItemModal :items="currentMenuStore.menuItemsRaw" :category="subcategory.name" @addItem="addItem">
                </MenuItemModal>
                <div class="toggle-collapse" @click="collapsed = !collapsed" :class="{ collapsed }">
                    <svg-vue icon="arrow-toggle"></svg-vue>
                </div>
            </div>
        </div>

        <draggable group="subcategory" :list="subcategory.contents" item-key="name-sub" handle=".handle"
            @change="forceRerender" v-if="renderChildren">
            <template #item="{ element, index }">
                <MenuItem :item="{ ...element }" v-if="!collapsed" :categoryIndex="categoryIndex"
                    :subcategoryIndex="subcategoryIndex" :type="type" :itemIndex="index" :key="element.name"
                    @rerender="forceRerender">
                </MenuItem>
            </template>
        </draggable>
        <div class="delete-btn-container" v-if="!collapsed">
            <ConfirmModalVue :method="deleteSubcategory" :btn-content="'Delete ' + subcategory.name + ' Subcategory'">
                Are you sure you want to delete this subcategory?
            </ConfirmModalVue>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.toggle-collapse {
    margin-left: 40px;
}

.menu-item-holder {
    &__header {
        background: #fff;
        padding: var(--grid-padding);
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin: 2px 0 2px;

        @media (max-width: 600px) {
            display: block;
        }

        &--right {
            display: flex;
            align-items: center;

            @media (max-width: 600px) {
                justify-content: space-between;
                margin-top: 10px;
            }
        }
    }

    &--subcategory {
        width: 98%;
        margin-left: auto;
    }
}

.handle {
    margin-right: 20px;
    margin-bottom: 4px;
}

.d-flex {
    display: flex;
    align-items: center;
}

.delete-btn-container {
    margin-left: auto;
    margin-top: 2px;
    width: fit-content;
}
</style>
